import React, { useEffect, useState } from 'react';
import { Share, MoreVertical, Plus, Download, ChevronDown } from 'lucide-react';

function detectPlatform() {
  if (typeof navigator === 'undefined') return 'unknown';
  const ua = navigator.userAgent || '';
  if (/iPhone|iPad|iPod/i.test(ua)) return 'ios';
  if (/Android/i.test(ua)) return 'android';
  return 'desktop';
}

export default function SetupInstallScreen() {
  const [platform, setPlatform] = useState('unknown');

  useEffect(() => {
    setPlatform(detectPlatform());
  }, []);

  return (
    <div style={styles.screen}>
      <Logo />

      <div style={styles.card}>
        <h1 style={styles.title}>Add VCIN to your home screen</h1>
        <p style={styles.subtitle}>
          One more step. Add VCIN to your phone's home screen so you can open it like any other app.
        </p>

        {platform === 'ios' && <IosSteps />}
        {platform === 'android' && <AndroidSteps />}
        {platform === 'desktop' && <DesktopMessage />}
        {platform === 'unknown' && <FallbackSteps />}

        <div style={styles.footerBox}>
          <p style={styles.footerText}>
            Once you've added VCIN, tap the icon on your home screen to open the app. You're all set.
          </p>
        </div>
      </div>
    </div>
  );
}

function IosSteps() {
  return (
    <div style={styles.stepsContainer}>
      <Step
        number={1}
        icon={<Share size={36} color="#001a4d" strokeWidth={2} />}
        title="Tap the Share icon"
        detail="Look at the bottom of Safari, in the middle of the toolbar."
      />
      <Step
        number={2}
        icon={<Plus size={36} color="#001a4d" strokeWidth={2.5} />}
        title="Tap 'Add to Home Screen'"
        detail="Scroll down the share menu if you don't see it right away."
      />
      <Step
        number={3}
        icon={<ChevronDown size={36} color="#001a4d" strokeWidth={2} style={{ transform: 'rotate(180deg)' }} />}
        title="Tap 'Add'"
        detail="The blue 'Add' button is in the top right corner."
      />
    </div>
  );
}

function AndroidSteps() {
  return (
    <div style={styles.stepsContainer}>
      <Step
        number={1}
        icon={<MoreVertical size={36} color="#001a4d" strokeWidth={2.5} />}
        title="Tap the three-dot menu"
        detail="Top right corner of Chrome."
      />
      <Step
        number={2}
        icon={<Download size={36} color="#001a4d" strokeWidth={2} />}
        title="Tap 'Install app'"
        detail="Sometimes shown as 'Add to Home Screen'."
      />
      <Step
        number={3}
        icon={<Plus size={36} color="#001a4d" strokeWidth={2.5} />}
        title="Tap 'Install' to confirm"
        detail="A small dialog will appear — tap 'Install'."
      />
    </div>
  );
}

function DesktopMessage() {
  return (
    <div style={styles.desktopBox}>
      <p style={styles.desktopText}>
        <strong>VCIN is designed for mobile.</strong>
      </p>
      <p style={styles.desktopText}>
        On your phone, tap the link in your invitation email. You'll be guided through setup and installation there.
      </p>
    </div>
  );
}

function FallbackSteps() {
  return (
    <div style={styles.stepsContainer}>
      <div style={styles.fallbackBlock}>
        <h3 style={styles.fallbackTitle}>iPhone (Safari)</h3>
        <p style={styles.fallbackText}>
          Tap the <strong>Share</strong> icon at the bottom of Safari → tap <strong>Add to Home Screen</strong> → tap <strong>Add</strong>.
        </p>
      </div>
      <div style={styles.fallbackBlock}>
        <h3 style={styles.fallbackTitle}>Android (Chrome)</h3>
        <p style={styles.fallbackText}>
          Tap the <strong>three-dot menu</strong> in the top right → tap <strong>Install app</strong> → tap <strong>Install</strong>.
        </p>
      </div>
    </div>
  );
}

function Step({ number, icon, title, detail }) {
  return (
    <div style={styles.step}>
      <div style={styles.stepHeader}>
        <div style={styles.stepNumber}>{number}</div>
        <div style={styles.iconCircle}>{icon}</div>
      </div>
      <p style={styles.stepTitle}>{title}</p>
      <p style={styles.stepDetail}>{detail}</p>
    </div>
  );
}

function Logo() {
  return (
    <div style={{ textAlign: 'center', marginBottom: '32px' }}>
      <div style={{
        background: '#F5C400', border: '3px solid #111',
        borderRadius: '8px', display: 'inline-block',
        padding: '10px 28px', marginBottom: '16px',
      }}>
        <div style={{
          fontFamily: "'Oswald', sans-serif", fontSize: '36px',
          fontWeight: 700, color: '#111', letterSpacing: '8px',
        }}>VCIN</div>
      </div>
      <div style={{
        fontFamily: "'Oswald', sans-serif", fontSize: '11px',
        fontWeight: 600, letterSpacing: '3px', color: 'rgba(255,255,255,0.5)',
        textTransform: 'uppercase',
      }}>Vehicle Crime Intelligence Network</div>
    </div>
  );
}

const styles = {
  screen: {
    minHeight: '100vh', background: '#001a4d',
    display: 'flex', flexDirection: 'column',
    alignItems: 'center', justifyContent: 'flex-start',
    padding: '32px 24px', fontFamily: "'Inter', sans-serif",
  },
  card: {
    background: 'white', borderRadius: '16px', padding: '28px 24px',
    width: '100%', maxWidth: '420px',
    boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
  },
  title: {
    fontFamily: "'Oswald', sans-serif", fontSize: '22px',
    fontWeight: 700, color: '#001a4d', letterSpacing: '1px',
    marginTop: 0, marginBottom: '12px',
  },
  subtitle: {
    fontSize: '14px', color: '#4a5a7a', lineHeight: 1.6,
    marginBottom: '20px',
  },
  stepsContainer: {
    display: 'flex', flexDirection: 'column', gap: '24px',
    marginTop: '24px', marginBottom: '24px',
  },
  step: {
    background: '#f7f8fc',
    border: '1px solid #e4e8f0',
    borderRadius: '12px',
    padding: '20px 16px',
  },
  stepHeader: {
    display: 'flex', flexDirection: 'row',
    alignItems: 'center', gap: '14px',
    marginBottom: '12px',
  },
  stepNumber: {
    width: '32px', height: '32px', borderRadius: '50%',
    background: '#001a4d', color: 'white',
    fontFamily: "'Oswald', sans-serif", fontSize: '16px',
    fontWeight: 700, display: 'flex', alignItems: 'center',
    justifyContent: 'center', flexShrink: 0,
  },
  iconCircle: {
    width: '64px', height: '64px', borderRadius: '50%',
    background: 'white', border: '2px solid #e4e8f0',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    boxShadow: '0 2px 6px rgba(0,0,0,0.04)',
    flexShrink: 0,
  },
  stepTitle: {
    fontFamily: "'Oswald', sans-serif", fontSize: '15px',
    fontWeight: 700, color: '#001a4d', letterSpacing: '0.5px',
    marginTop: 0, marginBottom: '6px',
  },
  stepDetail: {
    fontSize: '13px', color: '#4a5a7a', lineHeight: 1.5,
    margin: 0,
  },
  desktopBox: {
    background: '#fffbf0', border: '1px solid #f0d890',
    borderLeft: '3px solid #C9973A', borderRadius: '8px',
    padding: '14px 16px', marginTop: '16px',
  },
  desktopText: {
    fontSize: '14px', color: '#92400E', lineHeight: 1.6, margin: '0 0 8px 0',
  },
  fallbackBlock: {
    padding: '14px 16px',
    background: '#f7f8fc',
    border: '1px solid #e4e8f0',
    borderRadius: '10px',
  },
  fallbackTitle: {
    fontFamily: "'Oswald', sans-serif", fontSize: '13px',
    fontWeight: 700, color: '#001a4d', letterSpacing: '1px',
    textTransform: 'uppercase',
    marginTop: 0, marginBottom: '6px',
  },
  fallbackText: {
    fontSize: '13px', color: '#4a5a7a', lineHeight: 1.6, margin: 0,
  },
  footerBox: {
    marginTop: '20px', paddingTop: '20px',
    borderTop: '1px solid #e4e8f0',
  },
  footerText: {
    fontSize: '13px', color: '#6b7a94', lineHeight: 1.6,
    textAlign: 'center', margin: 0,
  },
};
